// Company intel (record-page redesign step 6, v2 only).
//   window.ContactCompanyCard   the person's company on the contact page: the classic folded
//                               'Seller brief · <company>' (contact.seller_brief), then the
//                               firmographic Company card, tier + implied value, folded (contact.company)
//   window.CompanyIntelBody     ?panel=intel (PanelHost → RecordDrawer 640): Facts & fit ·
//                               Firmographics (SourceScrub) · Business (description, services);
//                               a deal guest's: the Seller brief alone
//   window.CompanyIntelKit      pure helpers (atlas-crm/test/market_intel.test.mjs)
// The drawer is the firmographic intel only: on every page the seller brief
// stays its own folded card (Decisions 2026-10-13: the brief belongs with
// MyExit, apart from Company intel). A deal guest's drawer is the brief alone,
// read-only and read by engagement_id. Company-scoped on deals and people
// (ctx.companyId).

const INTEL_BLOCKS = Object.freeze(['brief', 'facts', 'firmographics', 'about']);
// The ?focus= targets (spec URL: focus=brief|facts|firmographics; record_panels' intel.valid).
const INTEL_FOCUS = Object.freeze(['brief', 'facts', 'firmographics']);
// The drawer's blocks: a deal guest gets the brief only (and never asks for
// facts, the company or its people); everyone else the firmographic blocks.
const intelBlocksFor = ({ guest = false } = {}) => (guest ? ['brief'] : INTEL_BLOCKS.filter((b) => b !== 'brief'));
// ?focus= picks the block scrolled into view; anything else is the first block shown.
const intelFocusOf = (focus, blocks) => {
  const list = blocks || INTEL_BLOCKS;
  return focus && INTEL_FOCUS.includes(focus) && list.includes(focus) ? focus : list[0];
};
// The contact card's folded line: "Strong · 6.8× → ~$193.1M"; nothing is —.
const intelContactPreview = ({ tier, implied } = {}) => (!tier && !implied ? '—'
  : [window.IntelKit.tierLabel(tier), implied].filter(Boolean).join(' · '));
window.CompanyIntelKit = Object.freeze({ BLOCKS: INTEL_BLOCKS, FOCUS: INTEL_FOCUS, blocksFor: intelBlocksFor,
  focusOf: intelFocusOf, contactPreview: intelContactPreview });

const CI_DIM = { color: 'var(--text-4)' };
const CI_LINK = { background: 'none', border: 'none', padding: 0, cursor: 'pointer', font: 'inherit', textAlign: 'left', color: 'var(--accent-text)' };
const CI_HEAD = { display: 'flex', alignItems: 'center', gap: 8, padding: '10px 0 6px', minWidth: 0 };
const CI_TITLE = { fontSize: 13, fontWeight: 600, color: 'var(--text)', flexShrink: 0 };
// Block rules span the drawer edge to edge: the Drawer body pads 16px (primitives.jsx Drawer), so -16px
// out and 16px back in. SourceScrubPanel brings its own top rule and 14px inset, so its block only bleeds.
const CI_RULE = { borderTop: '1px solid var(--border)', margin: '6px -16px 0', padding: '0 16px' };
const CI_BLEED = { margin: '6px -16px 0' };

const ContactCompanyCard = ({ contactId, companyId, companyName, variant = 'card' }) => {
  const sum = window.useRecordSummary('contact', contactId);
  const [cold, setCold] = React.useState(null);     // { cid, stats }: the card's own read, for that company only
  const asked = React.useRef(null);                 // the company the cold read was made for
  const alive = React.useRef(true);
  React.useEffect(() => { alive.current = true; return () => { alive.current = false; }; }, []);
  const K = window.CompsKit;
  const s = sum.data || null;
  // The summary speaks for this company once it has caught up with a change of company.
  const here = K.summaryHere(s, companyId);
  // The same cold-cache rule as the Market card: one companyComps read per company.
  const needCold = !!contactId && !!companyId && !sum.loading && here && s.comps == null;
  React.useEffect(() => {
    if (!needCold || asked.current === companyId) return;
    asked.current = companyId;
    const cid = companyId;
    Promise.resolve(window.AtlasApi.companyComps(cid))
      .then((p) => { if (alive.current && p) setCold({ cid, stats: K.cardStats(p) }); }).catch(() => {});
  }, [needCold, companyId]);   // eslint-disable-line react-hooks/exhaustive-deps
  if (!contactId || !companyId) return null;
  const brief = (here && s.brief) || null;
  const st = K.cardNumbers(s, companyId, cold);
  const implied = K.implied(st);
  const near = st && st.nearest_sale;
  const openMarketTab = (tab) => window.openRecordPanel('market', { tab }, { via: 'button' });
  const expand = window.recordPanelAvailable('intel', window.recordSurface()) ? (
    <window.Btn size="xs" variant="ghost" title="Open" icon={<window.Icon.Maximize size={11} />}
      onClick={() => window.openRecordPanel('intel', { focus: 'facts' }, { via: 'button' })} />
  ) : null;
  const card = (
    <window.Section variant={variant} title="Company" action={expand}
      collapsible id="contact.company" defaultOpen={false}
      preview={sum.loading ? '—' : intelContactPreview({ tier: brief && brief.tier, implied })}>
      <div style={{ padding: '10px 14px 12px', display: 'flex', flexDirection: 'column', gap: 8, fontSize: 12.5, lineHeight: 1.5, color: 'var(--text)' }}>
        <button type="button" style={{ ...CI_LINK, color: 'var(--text)', fontWeight: 600 }}
          onClick={() => window.AtlasNav.goToCompany(companyId)}>{companyName || '—'}</button>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
          {window.IntelTierPill && <window.IntelTierPill tier={brief && brief.tier} />}
          <button type="button" style={CI_LINK} onClick={() => openMarketTab('market')}>
            {implied ? `Median ${implied}` : <span style={CI_DIM}>Median —</span>}
          </button>
          {near && near.company && (
            <button type="button" style={CI_LINK} onClick={() => openMarketTab('nearby')}>
              {`${near.company} sold${near.distance_mi != null ? ` · ${near.distance_mi} mi` : ''}`}
            </button>
          )}
        </div>
      </div>
    </window.Section>
  );
  // The seller brief stays its own card, as on the deal and company pages: the
  // classic contact mount (same id, title and fold), read-only for a viewer.
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: variant === 'rail' ? 0 : 14 }}>
      <window.SellerBriefSection companyId={companyId} sectionId="contact.seller_brief"
        title={`Seller brief · ${companyName || 'their company'}`} readOnly={!window.recordCanWrite()} />
      {card}
    </div>
  );
};

const IntelField = ({ label, value, canWrite, onSave }) => (
  <div style={{ padding: '2px 0 8px' }}>
    <div style={{ fontSize: 11.5, color: 'var(--text-3)', marginBottom: 2 }}>{label}</div>
    <div style={{ fontSize: 12, color: 'var(--text)' }}>
      <window.EditableCell type="multiline" label={label} value={value || ''} editable={canWrite} onSave={onSave} />
    </div>
  </div>
);

const CompanyIntelBody = ({ api }) => {
  const kind = api.rec.kind;
  const ctx = api.ctx || {};
  const guest = !!api.guest;
  const canWrite = !!api.canWrite;
  const cid = kind === 'company' ? api.rec.id : (ctx.companyId || null);
  // The brief is its own card on every page (the 10-13 Decision); a deal guest reads the brief alone.
  const blocks = intelBlocksFor({ guest });
  const focus = intelFocusOf(api.params && api.params.focus, blocks);
  const rootRef = React.useRef(null);
  const touched = React.useRef(false);
  const [fetched, setFetched] = React.useState(null);     // the company, when the page has none in ctx
  const [contacts, setContacts] = React.useState(null);   // deal / contact pages: SourceScrub '+ Add' dedupe
  const company = ctx.company || fetched;
  React.useEffect(() => {
    if (guest || !cid || ctx.company || fetched) return undefined;
    let alive = true;
    window.AtlasApi.getCompany(cid)
      .then((raw) => { if (alive && raw) setFetched(window.AtlasData.__adaptCompany(raw)); }).catch(() => {});
    return () => { alive = false; };
  }, [guest, cid, !!ctx.company]);   // eslint-disable-line react-hooks/exhaustive-deps
  React.useEffect(() => {
    if (guest || !canWrite || !cid || kind === 'company') return undefined;
    let alive = true;
    window.AtlasApi.listContacts({ company_id: cid, limit: 200 })
      .then((res) => { if (alive) setContacts(((res && res.items) || []).map(window.AtlasData.__adaptContact)); })
      .catch(() => { if (alive) setContacts([]); });
    return () => { alive = false; };
  }, [guest, canWrite, cid, kind]);
  // ?focus= → its block scrolls into view, on open and on change; once more after
  // the blocks above have loaded, unless the viewer has scrolled meanwhile.
  React.useEffect(() => {
    if (!api.active) return undefined;
    touched.current = false;
    const go = () => {
      const root = rootRef.current;
      const el = root && root.querySelector(`[data-intel-block="${focus}"]`);
      if (el && el.scrollIntoView) el.scrollIntoView({ block: 'start' });
    };
    go();
    const t = setTimeout(() => { if (!touched.current) go(); }, 400);
    return () => clearTimeout(t);
  }, [focus, api.active]);
  const onCompanyChange = (raw) => {
    if (ctx.onCompanyChange) ctx.onCompanyChange(raw);
    else if (raw) setFetched(window.AtlasData.__adaptCompany(raw));
  };
  const onContactCreated = ctx.onContactCreated
    || ((c) => setContacts((prev) => [window.AtlasData.__adaptContact(c), ...(prev || [])]));
  // Atlas-only fields (no DealCloud lock, no ui_pins): the canonical description and services.
  const saveField = async (field, v) => {
    try {
      const updated = await window.AtlasApi.updateCompany(cid, { [field]: v || null });
      onCompanyChange(updated);
      window.pushToast({ type: 'success', title: 'Saved' });
    } catch (e) {
      window.pushToast({ type: 'error', title: 'Save failed', body: String((e && e.message) || e) });
      throw e;     // EditableCell reverts
    }
  };
  if (!cid) return <span style={CI_DIM}>—</span>;
  const raw = (company && company._raw) || {};
  // No SourceScrub data and nothing to enrich (or the company not loaded yet): the heading and a dimmed —.
  const ssEmpty = (
    <div style={{ borderTop: '1px solid var(--border)', padding: '0 16px' }}>
      <div style={CI_HEAD}><h3 style={CI_TITLE}>SourceScrub</h3></div>
      <div style={{ ...CI_DIM, paddingBottom: 10 }}>—</div>
    </div>
  );
  return (
    <div ref={rootRef} onWheel={() => { touched.current = true; }} onTouchStart={() => { touched.current = true; }}>
      {blocks.includes('brief') && (
        <div data-intel-block="brief">
          {kind === 'deal'
            ? <window.SellerBriefSection deal={api.record} bare readOnly={!canWrite} />
            : <window.SellerBriefSection companyId={cid} bare readOnly={!canWrite} />}
        </div>
      )}
      {blocks.includes('facts') && (
        <div data-intel-block="facts" style={blocks[0] === 'facts' ? undefined : CI_RULE}>
          <window.CompanyFactsSection companyId={cid} bare readOnly={!canWrite} />
        </div>
      )}
      {blocks.includes('firmographics') && (
        <div data-intel-block="firmographics" style={CI_BLEED}>
          {company && window.SourceScrubPanel ? (
            <window.SourceScrubPanel companyId={cid} company={company}
              contacts={kind === 'company' ? (ctx.contacts || null) : contacts}
              onCompanyChange={onCompanyChange} onContactCreated={onContactCreated} readOnly={!canWrite} fallback={ssEmpty} />
          ) : ssEmpty}
        </div>
      )}
      {blocks.includes('about') && (
        <div data-intel-block="about" style={CI_RULE}>
          <div style={CI_HEAD}><h3 style={CI_TITLE}>Business</h3></div>
          <IntelField label="Business description" value={raw.business_description} canWrite={canWrite && !!company}
            onSave={(v) => saveField('business_description', v)} />
          <IntelField label="Services" value={raw.services} canWrite={canWrite && !!company} onSave={(v) => saveField('services', v)} />
        </div>
      )}
    </div>
  );
};

window.ContactCompanyCard = ContactCompanyCard;
window.CompanyIntelBody = CompanyIntelBody;
